import { useContext, useEffect, useRef, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { trackMetaEvent } from '../utils/metaPixel';
import AppContext from "../context";

import LogOut from './LogOut';

import Entry from './entry/Entry';
import Login from './entry/forms/Login';
import CreateAccount from './entry/forms/CreateAccount';
import VerifyEmailAddress from './entry/forms/VerifyEmailAddress';
import Induction from './entry/forms/Induction';
import RequestInduction from './entry/forms/RequestInduction';
import Terms from './entry/forms/Terms';
import HealthScreening from './entry/forms/HealthScreening';
import ForgottenPassword from './entry/forms/ForgottenPassword';
import ResetPassword from './entry/forms/ResetPassword';

import Dashboard from './dashboard/Dashboard';
import Book from "./dashboard/book/Book";
import Confirm from "./dashboard/book/Confirm";
import Join from "./dashboard/book/Join";
import Bookings from "./dashboard/bookings/Bookings";
import Tokens from "./dashboard/tokens";
import Account from "./dashboard/account/Account";
import AddMinor from "./dashboard/account/AddMinor";
import Membership from "./dashboard/membership/Membership";
import PersonalAccessCode from "./dashboard/personalAccessCode/PersonalAccessCode";

// Service Booking (Clinic/Salon)
import ServiceList from "./dashboard/services/ServiceList";
import ServiceBook from "./dashboard/services/ServiceBook";
import ServiceBookingDetail from "./dashboard/services/ServiceBookingDetail";
import MyServiceBookings from "./dashboard/services/MyServiceBookings";

import Admin from './admin/Admin';
import Users from './admin/users';
import AdminBookings from './admin/bookings';
import Products from './admin/products';
import MembershipPlans from "./admin/membershipPlans";
import Studios from './admin/studios';
import Withheld from './admin/studios/withheld';
import Transactions from './admin/transactions';
import Minors from './admin/minors';
import DiscountCodes from './admin/discountCodes';
import AdminServices from './admin/services';
import AdminStaff from './admin/staff';
import AdminAvailability from './admin/availability';
import AdminServiceBookings from './admin/serviceBookings';
import AdminSettings from './admin/settings';
import AdminHealthScreening from './admin/healthScreening';

import GiftVouchersTemplate from "./giftVouchers";
import GiftVouchers from "./giftVouchers/GiftVouchers";
import Checkout from "./giftVouchers/Checkout";

import NetworkError from "./NetworkError";
import SiteNotFound from "./SiteNotFound";
import {Network} from "@capacitor/network";
import Members from "./admin/members";
import Coupons from "./admin/coupons";
import WalletTransactions from "./admin/walletTransactions";
import MembershipTransactions from "./admin/membershipTransactions";
import ExperiencesRouter from "./experiences/Router";
import ExperienceLayout from "./experiences/Layout";
import PublicClasses from "./experiences/Experiences";
import PublicClass from "./experiences/Experience";
import ExperienceCheckout from "./experiences/checkout/Checkout";
import ExperienceBookNow from "./experiences/checkout/BookNow";
import ExperienceHealthScreening from "./experiences/checkout/HealthScreening";
import ExperienceBundleBook from "./experiences/checkout/BundleBook";
import ExperienceCreateAccount from "./experiences/checkout/CreateAccount";
import ExperienceConfirm from "./experiences/checkout/Confirm";
import ExperienceBundleConfirm from "./experiences/checkout/BundleConfirm";
import ExperienceContact from "./experiences/Contact";
import ExperienceClasses from "./experiences/admin/experiences/Experiences";
import ExperienceClass from "./experiences/admin/experiences/Experience";
import CreateExperienceClass from "./experiences/admin/experiences/Create";
import EditExperienceClass from "./experiences/admin/experiences/Edit";
import AddExperienceClassEvents from "./experiences/admin/experiences/AddEvents";
import UpcomingClassEvents from "./experiences/admin/upcomingEvents/UpcomingEvents";
import ClassEvent from "./experiences/admin/upcomingEvents/Event";
import OverrideClassEventSettings from "./experiences/admin/upcomingEvents/OverrideSettings";
import ClassBookings from "./experiences/admin/bookings/Bookings";
import ClassBooking from "./experiences/admin/bookings/Booking";
import BundleCreate from "./experiences/admin/bundles/Create";
import BundleView from "./experiences/admin/bundles/View";
import VariableRouter from "./VariableRouter";
import SuperAdminRouter from "./superAdmin/Router";
import OnboardingCustomer from "./public/OnboardingCustomer";

function PageViewTracker() {
	const context = useContext(AppContext);
	const location = useLocation();
	const mountedRef = useRef(false);
	const pixelId = context.domain?.metaPixelEnabled ? context.domain?.metaPixelId : null;

	useEffect(() => {
		if (!pixelId) return;
		if (!mountedRef.current) { mountedRef.current = true; return; } // skip initial — App.js fires on load
		trackMetaEvent({ pixelId, eventName: 'PageView' });
	}, [location.pathname, pixelId]);

	return null;
}

const STANDARD_BUSINESS_TYPES = ['sessions', 'membership', 'experiences', 'hybrid', 'hybrid-new'];
const SERVICE_BUSINESS_TYPES = ['clinic', 'salon', 'restaurant'];
const GYM_BUSINESS_TYPES = ['sessions', 'membership', 'hybrid', 'hybrid-new'];

const ClassConfirmRedirect = () => {
	const location = useLocation();
	return <Navigate to={`../create-account${location.search}`} />;
};

function EntryRoutes() {
	return (
		<>
			<Route path="entry" element={<Entry />}>
				<Route path="login" element={<Login />} />
				<Route path="create-account" element={<CreateAccount />} />
				<Route path="forgotten-password" element={<ForgottenPassword />} />
				<Route path="reset-password/:token" element={<ResetPassword />} />
			</Route>
			<Route path="*" element={<Navigate to={`/entry/login?referer=${window.location.pathname+window.location.search}`} />} />
		</>
	)
}

function VerifyEmailAddressRoutes() {
	return (
		<>
			<Route path="entry" element={<Entry />}>
				<Route path="verify-email-address" element={<VerifyEmailAddress />} />
			</Route>
			<Route path="*" element={<Navigate to="/entry/verify-email-address" />} />
		</>
	)
}

function TermRoutes() {
	const context = useContext(AppContext);

	return (
		<>
			<Route path="entry" element={<Entry />}>
				{context.domain.videoInduction ?
					<Route path="induction" element={<Induction />} />
				:
					<Route path="request-induction" element={<RequestInduction />} />
				}
				<Route path="terms" element={<Terms />} />
			</Route>
			<Route path="*" element={context.domain.videoInduction ? <Navigate to="/entry/induction" /> : <Navigate to="/entry/request-induction" />} />
		</>
	)
}

function HealthScreeningRoutes() {
	return (
		<>
			<Route path="entry" element={<Entry />}>
				<Route path="health-screening" element={<HealthScreening />} />
			</Route>
			<Route path="*" element={<Navigate to="/entry/health-screening" />} />
		</>
	)
}

function DashboardRoutes() {
	return (
		<>
			<Route exact path="dashboard" element={<Dashboard />}>
				<Route exact path="book" element={<Book />}>
					<Route exact path=":studio/sessions/:week" element={<Book />} />
				</Route>
				<Route exact path="book/:studio/confirm" element={<Confirm />} />
				<Route exact path="book/join/:shareLink" element={<Join />} />
				<Route exact path="bookings" element={<Bookings />} />
				<Route exact path="membership" element={<Membership />} />
				<Route exact path="personal-access-code" element={<PersonalAccessCode />} />
				<Route exact path="tokens" element={<Tokens.Template />}>
					<Route exact path="" element={<Tokens.List />} />
					<Route exact path="checkout/:id" element={<Tokens.Checkout />} />
				</Route>
				<Route exact path="settings/add-minor" element={<AddMinor />} />
				<Route exact path="settings" element={<Account />} />
			</Route>
			<Route path="*" element={<Navigate to="/dashboard/book" />} />
		</>
	)
}

function ServicesDashboardRoutes() {
	return (
		<>
			<Route exact path="dashboard" element={<Dashboard />}>
				{/* Service Booking Routes (Clinic/Salon) */}
				<Route exact path="services" element={<ServiceList />} />
				<Route exact path="services/:id" element={<ServiceBook />} />
				<Route exact path="service-bookings/:id" element={<ServiceBookingDetail />} />
				<Route exact path="my-service-bookings" element={<MyServiceBookings />} />
				<Route exact path="settings/add-minor" element={<AddMinor />} />
				<Route exact path="settings" element={<Account />} />
			</Route>
			<Route path="*" element={<Navigate to="/dashboard/services" />} />
		</>
	)
}

function MembershipDashboardRoutes() {
	return (
		<>
			<Route exact path="dashboard" element={<Dashboard />}>
				<Route exact path="settings/add-minor" element={<AddMinor />} />
				<Route exact path="settings" element={<Account />} />
				<Route exact path="membership" element={<Membership />} />
				<Route exact path="personal-access-code" element={<PersonalAccessCode />} />
			</Route>
			<Route path="*" element={<Navigate to="/dashboard/membership" />} />
		</>
	)
}

function AdminRoutes() {
	return (
		<>
			<Route exact path="admin" element={<Admin />}>
				<Route exact path="bookings" element={<AdminBookings.Template />}>
					<Route exact path="" element={<AdminBookings.List />} />
					<Route exact path=":id" element={<AdminBookings.Existing />} />
					<Route exact path="create" element={<AdminBookings.Create />} />
				</Route>
				<Route exact path="products" element={<Products.Template />}>
					<Route exact path="" element={<Products.List />} />
					<Route exact path="create" element={<Products.Create />} />
					<Route exact path=":id" element={<Products.Update />} />
				</Route>
				<Route exact path="users" element={<Users.Template />}>
					<Route exact path="" element={<Users.List />} />
					<Route exact path=":id" element={<Users.Existing />} />
				</Route>
				<Route exact path="minors" element={<Minors.Template />}>
					<Route exact path="" element={<Minors.List />} />
					<Route exact path=":id" element={<Minors.Existing />} />
				</Route>
				<Route exact path="transactions" element={<Transactions.Template />}>
					<Route exact path="" element={<Transactions.List />} />
					<Route exact path="create" element={<Transactions.Create />} />
					<Route exact path=":id" element={<Transactions.Existing />} />
				</Route>
				<Route exact path="studios" element={<Studios.Template />}>
					<Route exact path="" element={<Studios.List />} />
					<Route exact path="create" element={<Studios.Create />} />
					<Route exact path=":id" element={<Studios.Update />} />
					<Route exact path=":id/withheld/create" element={<Withheld.Create />} />
					<Route exact path=":id/withheld/:withheldId" element={<Withheld.Existing />} />
				</Route>
				<Route exact path="discount-codes" element={<DiscountCodes.Template />}>
					<Route exact path="" element={<DiscountCodes.List />} />
					<Route exact path="create" element={<DiscountCodes.Create />} />
					<Route exact path=":id" element={<DiscountCodes.Update />} />
				</Route>
				<Route exact path="upcoming-events" element={<UpcomingClassEvents />} />
				<Route exact path="upcoming-events/:id" element={<ClassEvent />} />
				<Route exact path="upcoming-events/:id/override-settings" element={<OverrideClassEventSettings />} />
				<Route exact path="class-bookings" element={<ClassBookings />} />
				<Route exact path="class-bookings/:id" element={<ClassBooking />} />
				<Route exact path="experiences" element={<ExperienceClasses />} />
				<Route exact path="experiences/create" element={<CreateExperienceClass />} />
				<Route exact path="experiences/:id" element={<ExperienceClass />} />
				<Route exact path="experiences/:id/edit" element={<EditExperienceClass />} />
				<Route exact path="experiences/:id/add-events" element={<AddExperienceClassEvents />} />
				<Route exact path="experiences/:id/bundles/create" element={<BundleCreate />} />
				<Route exact path="experiences/:id/bundles/:bundleId" element={<BundleView />} />
				<Route exact path="members" element={<Members.Template />}>
					<Route exact path="" element={<Members.List />} />
					<Route exact path=":id" element={<Members.Existing />} />
				</Route>
				<Route exact path="membership-plans" element={<MembershipPlans.Template />}>
					<Route exact path="" element={<MembershipPlans.List />} />
					<Route exact path="create" element={<MembershipPlans.Create />} />
					<Route exact path=":id" element={<MembershipPlans.Update />} />
				</Route>
				<Route exact path="membership-transactions" element={<MembershipTransactions.Template />}>
					<Route exact path="" element={<MembershipTransactions.List />} />
				</Route>
				<Route exact path="coupons" element={<Coupons.Template />}>
					<Route exact path="" element={<Coupons.List />} />
					<Route exact path="create" element={<Coupons.Create />} />
				</Route>
				<Route exact path="settings" element={<AdminSettings.Template />}>
					<Route exact path="" element={<AdminSettings.Settings />} />
				</Route>
				<Route exact path="health-screening" element={<AdminHealthScreening.Template />}>
					<Route exact path="" element={<AdminHealthScreening.List />} />
					<Route exact path="questions" element={<AdminHealthScreening.Questions />} />
				</Route>
			</Route>
			<Route path="*" element={<Navigate to="/admin/products" />} />
		</>
	)
}

function ServicesAdminRoutes() {
	return (
		<>
			<Route exact path="admin" element={<Admin />}>
				<Route exact path="services" element={<AdminServices.Template />}>
					<Route exact path="" element={<AdminServices.List />} />
					<Route exact path="create" element={<AdminServices.Create />} />
					<Route exact path=":id" element={<AdminServices.Update />} />
				</Route>
				<Route exact path="staff" element={<AdminStaff.Template />}>
					<Route exact path="" element={<AdminStaff.List />} />
					<Route exact path="create" element={<AdminStaff.Create />} />
					<Route exact path=":id" element={<AdminStaff.Update />} />
				</Route>
				<Route exact path="staff/:id/report" element={<AdminStaff.Report />} />
				<Route exact path="availability" element={<AdminAvailability.Template />}>
					<Route exact path="" element={<AdminAvailability.Manage />} />
				</Route>
				<Route exact path="service-bookings" element={<AdminServiceBookings.Template />}>
					<Route exact path="" element={<AdminServiceBookings.List />} />
					<Route exact path=":id" element={<AdminServiceBookings.Existing />} />
				</Route>
				<Route exact path="users" element={<Users.Template />}>
					<Route exact path="" element={<Users.List />} />
					<Route exact path=":id" element={<Users.Existing />} />
				</Route>
				<Route exact path="transactions" element={<Transactions.Template />}>
					<Route exact path="" element={<Transactions.List />} />
					<Route exact path="create" element={<Transactions.Create />} />
					<Route exact path=":id" element={<Transactions.Existing />} />
				</Route>
				<Route exact path="discount-codes" element={<DiscountCodes.Template />}>
					<Route exact path="" element={<DiscountCodes.List />} />
					<Route exact path="create" element={<DiscountCodes.Create />} />
					<Route exact path=":id" element={<DiscountCodes.Update />} />
				</Route>
				<Route exact path="wallet-transactions" element={<WalletTransactions.Template />}>
					<Route exact path="" element={<WalletTransactions.List />} />
				</Route>
				<Route exact path="settings" element={<AdminSettings.Template />}>
					<Route exact path="" element={<AdminSettings.Settings />} />
				</Route>
			</Route>
			<Route path="*" element={<Navigate to="/admin/services" />} />
		</>
	)
}

function MembershipAdminRoutes() {
	return (
		<>
			<Route exact path="admin" element={<Admin />}>
				<Route exact path="members" element={<Members.Template />}>
					<Route exact path="" element={<Members.List />} />
					<Route exact path=":id" element={<Members.Existing />} />
				</Route>
				<Route exact path="membership-plans" element={<MembershipPlans.Template />}>
					<Route exact path="" element={<MembershipPlans.List />} />
					<Route exact path="create" element={<MembershipPlans.Create />} />
					<Route exact path=":id" element={<MembershipPlans.Update />} />
				</Route>
				<Route exact path="transactions" element={<MembershipTransactions.Template />}>
					<Route exact path="" element={<MembershipTransactions.List />} />
				</Route>
				<Route exact path="coupons" element={<Coupons.Template />}>
					<Route exact path="" element={<Coupons.List />} />
					<Route exact path="create" element={<Coupons.Create />} />
				</Route>
				<Route exact path="settings" element={<AdminSettings.Template />}>
					<Route exact path="" element={<AdminSettings.Settings />} />
				</Route>
				<Route exact path="health-screening" element={<AdminHealthScreening.Template />}>
					<Route exact path="" element={<AdminHealthScreening.List />} />
					<Route exact path="questions" element={<AdminHealthScreening.Questions />} />
				</Route>
			</Route>
			<Route path="*" element={<Navigate to="/admin/members" />} />
		</>
	)
}

function Router() {
	const context = useContext(AppContext);
	const [loading, setLoading] = useState(true);
	const isVariableMode = !STANDARD_BUSINESS_TYPES.includes(context.domain?.businessType);
	const [networkDisconnected, setNetworkDisconnected] = useState(false);
	const [connectionFailed, setConnectionFailed] = useState(false);
	const [siteNotFound, setSiteNotFound] = useState(false);

	// useEffect(async () => {
	// 	const checkInitialNetworkStatus = async () => {
	// 		const status = await Network.getStatus();
	// 		setNetworkDisconnected(!status.connected);
	// 	};
	//
	// 	await checkInitialNetworkStatus();
	//
	// 	const listener = Network.addListener('networkStatusChange', (status) => {
	// 		setNetworkDisconnected(!status.connected);
	// 	});
	//
	// 	return () => {
	// 		listener.remove();
	// 	};
	// }, []);

	useEffect(() => {
		const bootstrap = async () => {
			try {
				await context.fetchDomain();
				await context.fetchUser();
				setLoading(false);
				setConnectionFailed(false);
			} catch(error) {
				if(error.response?.status === 401){
					await context.handle401();
					setConnectionFailed(false);
					setLoading(false);
				} else if(error.response?.status === 404) {
					setSiteNotFound(true);
					setConnectionFailed(false);
					setLoading(false);
				} else {
					setConnectionFailed(true);
					setLoading(false);
				}
			}
		};

		bootstrap();
	}, []);

	if(siteNotFound || context.domainNotFound) return <SiteNotFound />
	if(networkDisconnected || connectionFailed) return <NetworkError />
	if(loading) return null;

	if(context.domain?.businessType === 'onboarding') return <OnboardingCustomer />
	if(context.domain?.businessType === 'super-admin') return <SuperAdminRouter />
	if(context.domain.experiencesMode) return <ExperiencesRouter />
	if(isVariableMode) return <VariableRouter />


	return (
		<BrowserRouter>
			<PageViewTracker />
			<Routes>
				<Route path="log-out" element={<LogOut />} />
				<Route path="gift-vouchers" element={<GiftVouchersTemplate />}>
					<Route path="" element={<GiftVouchers />} />
					<Route path="checkout/:id" element={<Checkout />} />
				</Route>
				<Route path="classes" element={<ExperienceLayout />}>
					<Route path="contact" element={<ExperienceContact />} />
					<Route path=":id/checkout" element={<ExperienceCheckout basePath="/classes" />}>
						<Route path="health-screening" element={<ExperienceHealthScreening basePath="/classes" />} />
						<Route path="book-now" element={<ExperienceBookNow basePath="/classes" />} />
						<Route path="book-bundle" element={<ExperienceBundleBook basePath="/classes" />} />
						<Route path="create-account" element={<ExperienceCreateAccount />} />
						{context.user ?
							<Route path="confirm" element={<ExperienceConfirm basePath="/classes" />} />
						:
							<Route path="confirm" element={<ClassConfirmRedirect />} />
						}
						{context.user ?
							<Route path="bundle-confirm" element={<ExperienceBundleConfirm />} />
						:
							<Route path="bundle-confirm" element={<ClassConfirmRedirect />} />
						}
						<Route path="" element={<Navigate to="book-now" />} />
					</Route>
					<Route path=":id" element={<PublicClass basePath="/classes" />} />
					<Route path="" element={<PublicClasses basePath="/classes" title="Classes" />} />
				</Route>

				{!localStorage.getItem("token") ?
					EntryRoutes()
					: !context.user?.emailVerified ?
						VerifyEmailAddressRoutes()
						: !context.user?.terms && !isVariableMode ?
							TermRoutes()
							: context.domain.healthScreeningEnabled && !context.user?.healthScreening?.completedAt && !context.user.admin && GYM_BUSINESS_TYPES.includes(context.domain.businessType) ?
								HealthScreeningRoutes()
								: context.user.admin ?
									context.domain.membershipMode ?
										MembershipAdminRoutes()
										:
										SERVICE_BUSINESS_TYPES.includes(context.domain.businessType) ?
											ServicesAdminRoutes()
											:
											AdminRoutes()
								:
								 context.domain.membershipMode ?
									 MembershipDashboardRoutes()
									 :
									 SERVICE_BUSINESS_TYPES.includes(context.domain.businessType) ?
										 ServicesDashboardRoutes()
										 :
										 DashboardRoutes()
				}
			</Routes>
		</BrowserRouter>
	)
}

export default Router;
